/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import useProfileSizes from '~/pdp/utils/useProfileSizes';
import { getIsPlusProfile } from '~/shared/utils/getIsPlusProfile';
import { useBreakpoint } from '~/techstyle-shared/react-components';

import {
  buildDefaultImageUrlFromSku,
  buildFallbackImageFromCDN,
  buildImageUrlFromSku,
} from '../../utils/images';

const ProductCardImageFromSku = ({ sku, swatchLabel }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { profileSizes } = useProfileSizes({ isPdp: false });
  const isPlusProfile = getIsPlusProfile(profileSizes);
  const { isMobile } = useBreakpoint();

  // We set the initial image urls to the default image urls
  // to avoid flickering when the image is loading
  // TODO: this can be improved by using a skeleton loader
  // May be able to just use the buildImageUrlFromSku function on initial render
  // while we render the skeleton loader
  // We need the skeleton loader to be the same size as the image to avoid layout shift
  const [primaryImageUrl, setPrimaryImageUrl] = useState(() => {
    return buildDefaultImageUrlFromSku({
      sku,
      requestedAngle: '1',
      requestedSize: isMobile ? '327x491' : '577x866',
    });
  });

  const [hoverImageUrl, setHoverImageUrl] = useState(() => {
    return buildDefaultImageUrlFromSku({
      sku,
      requestedAngle: '2',
      requestedSize: isMobile ? '327x491' : '577x866',
    });
  });

  useEffect(() => {
    // If no sku is provided or we don't need to fetch for plus images,
    // we don't need to attempt to replace the default image urls with the plus image urls
    if (!sku || !isPlusProfile) {
      return;
    }

    const getImageUrls = async () => {
      const primaryImageUrl = await buildImageUrlFromSku({
        sku,
        isPlusProfile: true,
        requestedAngle: '1',
        requestedSize: isMobile ? '327x491' : '577x866',
      });
      const hoverImageUrl = await buildImageUrlFromSku({
        sku,
        isPlusProfile,
        requestedAngle: '2',
        requestedSize: isMobile ? '327x491' : '577x866',
      });
      setPrimaryImageUrl(primaryImageUrl);
      setHoverImageUrl(hoverImageUrl);
    };

    getImageUrls();
  }, [sku, isPlusProfile, isMobile]);

  const handleImageLoaded = (e) => {
    // TODO: handle skeleton loading by default, then smoothly transition skeleton loader to actual image
    // May need additional state to track loading
  };

  const handleImageError = (e) => {
    e.target.onerror = null; // prevents looping
    e.target.src = buildFallbackImageFromCDN({
      width: isMobile ? 327 : 577,
      height: isMobile ? 491 : 866,
    });
  };

  // Preload the hover image
  useEffect(() => {
    const image = new Image();
    image.src = hoverImageUrl;
  }, [hoverImageUrl]);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img
        src={isHovered ? hoverImageUrl : primaryImageUrl}
        alt={swatchLabel}
        title={swatchLabel}
        onError={handleImageError}
        onLoad={handleImageLoaded}
      />
    </div>
  );
};

ProductCardImageFromSku.propTypes = {
  sku: PropTypes.string.isRequired,
  swatchLabel: PropTypes.string.isRequired,
};

export default ProductCardImageFromSku;
